import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService, NOTIFICATIONS } from 'src/app/core/service/notifications.service';
import { AuthBackendService } from '../../../auth/service/auth-backend.service';
import { UserFirstLoginService } from 'src/app/services/first-login/user-first-login.service';
import { PwdInput } from '../../../shared/component/password-rules/classes/PwdInput';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PwdFormService } from "../../../shared/component/password-rules/classes/PwdFormService";
import { Validators } from "@angular/forms";
import { WEBSITE_PROD_ORCA_PRIVACY_POLICY, WEBSITE_PROD_ORCA_TERMS_OF_USE } from "../../../../../environments/urlService";
import { MOBILE_SCREEN_SIZE } from "../../../box/component/box.util";

@Component({
  selector: 'app-set-new-password',
  templateUrl: './set-new-password.component.html',
  styleUrls: [
    './set-new-password.component.scss',
    '../login.component.scss',
    '../entrance-group.scss',
  ],
})
export class SetNewPasswordComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  protected readonly WEBSITE_PROD_ORCA_TERMS_OF_USE = WEBSITE_PROD_ORCA_TERMS_OF_USE;
  protected readonly WEBSITE_PROD_ORCA_PRIVACY_POLICY = WEBSITE_PROD_ORCA_PRIVACY_POLICY;

  is: SetNewPasswordIs = {
    tokenValid: false,
    agreementAlreadySigned: false,
    dataFullyInit: false,
    formDisabled: false,
    mobileVersion: false,
  };

  pwdFormService = new PwdFormService();

  pwdInput = new PwdInput();
  confirmPwdInput = new PwdInput();

  constructor(
    private route: ActivatedRoute,
    private routerService: Router,
    private notificationsService: NotificationsService,
    private authBackendService: AuthBackendService,
    private firstLoginService: UserFirstLoginService,
  ) { }

  ngOnInit(): void {
    this.is.mobileVersion = window.screen.width <= MOBILE_SCREEN_SIZE;
    this.initTokenAndAgreementData();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  /**
   * Submits form and sets agreement (if it was not signed) and new password if all fields are valid.
   * Afterwards redirects to login with the credentials.
   * Login component receives the credentials via subscription and proceeds without user interaction.
   */
  async onSubmit(): Promise<void> {
    this.is.formDisabled = true;

    const { token } = this.route.snapshot.params as { token: string };
    const mail = this.getEmailQueryParameter();

    try {
      if (!this.pwdFormService.form.value.agreement) {
        this.notificationsService.error(NOTIFICATIONS.SET_NEW_PASSWORD_AGR_ERROR);
        this.is.formDisabled = false;
        return;
      }
      await this.authBackendService.setNewPassword(token, this.pwdFormService.form.controls.password.getRawValue());
      this.notificationsService.success(NOTIFICATIONS.SET_NEW_PASSWORD_SUCCESS);
      await this.routerService.navigate(['/login']);
      let data: UserAutoLoginDTO = {
        login: mail,
        pass: this.pwdFormService.form.controls.password.getRawValue(),
      };
      this.firstLoginService.changeData(data);
      data = undefined;
    } catch (err) {
      this.is.formDisabled = false;
    }
  }

  private initTokenAndAgreementData(): void {
    this.route.data
      .pipe(takeUntil(this._destroyStream))
      .subscribe((tokenInfo: { isTokenValid: boolean }) => {
        this.is.tokenValid = tokenInfo.isTokenValid;
        if (!this.is.tokenValid) {
          this.is.dataFullyInit = true;
          return;
        }
        this.initAgreement();
      });
  }

  private initAgreement(): void {
    const { token } = this.route.snapshot.params as { token: string };
    this.authBackendService.getAgreementStatus(token)
      .pipe(takeUntil(this._destroyStream))
      .subscribe((signed) => {
        this.is.agreementAlreadySigned = signed;
        this.pwdFormService.form.controls.agreement.setValue(this.is.agreementAlreadySigned);
        this.is.dataFullyInit = true;
      });
    this.pwdFormService.form.controls.agreement.setValidators([Validators.requiredTrue]);
  }

  /**
   * There are emails, which have '+' to use the same email address for different accounts.
   * The '+' is being wrongly encoded via browser, which from
   * 'test+1@test.com' encodes to 'test%201@test.com'
   * The problem is '%20' which is an encoded space.
   *
   * To fix this issue I manually change all spaces to pluses in the email.
   * I also trim the email to make sure that no spaces are before or after the email itself.
   *
   * @return an email with all ' ' changed to '+'.
   */
  private getEmailQueryParameter(): string {
    const email = this.route.snapshot.queryParamMap.get('email').trim();
    return email ? email.replace(/ /g, '+') : '';
  }
}

export interface UserAutoLoginDTO {
  login: string;
  pass: string;
}

interface SetNewPasswordIs {
  tokenValid: boolean;
  agreementAlreadySigned: boolean;
  dataFullyInit: boolean;
  formDisabled: boolean;
  mobileVersion: boolean;
}
